// apps/client/src/components/UpgradePromptCard.tsx
// Enhanced upgrade prompt with tier-specific messaging and glass-morphism styling

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, ArrowRight, Check } from 'lucide-react';
import { SubscriptionTier, getTierDisplayName, getTierPricing } from '../config/subscriptionTiers';

interface UpgradePromptCardProps {
  /** Feature name to display */
  featureName: string;
  /** Feature description */
  description: string;
  /** Tier required for this feature */
  requiredTier: SubscriptionTier;
  /** List of benefits/features included */
  benefits: string[];
  /** Custom CTA text */
  ctaText?: string;
  /** Custom handler instead of navigation */
  onUpgrade?: () => void;
}

/**
 * UpgradePromptCard - Full-page upgrade prompt with glass-morphism styling
 *
 * Used by FeatureGate when a user doesn't have access to a feature.
 * Displays the feature info, required tier, and benefits.
 */
const UpgradePromptCard: React.FC<UpgradePromptCardProps> = ({
  featureName,
  description,
  requiredTier,
  benefits,
  ctaText,
  onUpgrade,
}) => {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      navigate('/settings/billing');
    }
  };

  // Tier-specific gradient colors
  const tierGradients: Record<SubscriptionTier, { gradient: string; accent: string; shadow: string }> = {
    plus: {
      gradient: 'from-blue-500 to-cyan-500',
      accent: 'text-cyan-400',
      shadow: 'shadow-cyan-500/25',
    },
    pro: {
      gradient: 'from-purple-500 to-pink-500',
      accent: 'text-purple-400',
      shadow: 'shadow-purple-500/25',
    },
    enterprise: {
      gradient: 'from-amber-500 to-orange-500',
      accent: 'text-amber-400',
      shadow: 'shadow-amber-500/25',
    },
    // Internal lateral tier (Elevate). Never upsold, so this card should never
    // render for it — entry exists for Record exhaustiveness only.
    network: {
      gradient: 'from-slate-500 to-slate-600',
      accent: 'text-slate-300',
      shadow: 'shadow-slate-500/25',
    },
  };

  const tierStyle = tierGradients[requiredTier];

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gray-800/50 backdrop-blur-xl border border-gray-700/50">
      {/* Gradient accent line */}
      <div
        className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${tierStyle.gradient}`}
      />

      {/* Background decoration */}
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-48 h-48 bg-gradient-to-tr from-cyan-500/10 to-transparent rounded-full blur-3xl" />

      <div className="relative p-8">
        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          {/* Lock Icon */}
          <div className={`flex-shrink-0 p-4 rounded-2xl bg-gradient-to-br ${tierStyle.gradient} shadow-lg ${tierStyle.shadow}`}>
            <Lock className="w-6 h-6 text-white" />
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-semibold uppercase tracking-wider ${tierStyle.accent}`}>
                {getTierDisplayName(requiredTier)} Feature
              </span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{featureName}</h2>
            <p className="text-gray-400">{description}</p>
          </div>
        </div>

        {/* Benefits list */}
        <div className="mb-8">
          <h4 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-4">
            What you will get
          </h4>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center text-sm text-gray-300">
                <div className={`flex-shrink-0 p-1 rounded-full bg-gradient-to-r ${tierStyle.gradient} mr-3`}>
                  <Check className="w-3 h-3 text-white" />
                </div>
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        {/* Upgrade CTA */}
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <button
            onClick={handleUpgrade}
            className={`
              flex-1 sm:flex-none flex items-center justify-center gap-2
              px-6 py-3 rounded-xl font-semibold text-white
              bg-gradient-to-r ${tierStyle.gradient}
              hover:opacity-90 transition-all duration-200
              shadow-lg ${tierStyle.shadow}
            `}
          >
            <Sparkles className="w-5 h-5" />
            {ctaText || `Upgrade to ${getTierDisplayName(requiredTier)}`}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="text-center sm:text-left">
            <span className="text-gray-400 text-sm">Starting at </span>
            <span className="text-white font-semibold">{getTierPricing(requiredTier)}</span>
          </div>
        </div>

        {/* Contact link */}
        <p className="mt-6 text-center text-sm text-gray-500">
          Questions about pricing?{' '}
          <a
            href="mailto:support@valutten.com"
            className={`${tierStyle.accent} hover:underline`}
          >
            Contact our team
          </a>
        </p>
      </div>
    </div>
  );
};

export default UpgradePromptCard;
